import "@bosch-dock/pau-web-components";
import deepmerge from "deepmerge";
import { Component, ComponentInterface, Element, h, Listen, Prop, State, Watch } from "@stencil/core/internal";
import { ConsentService, GoogleConsentService, I18nService, JsonConfigurationService, LogService } from "../../services";
import { GoogleConsentApiType, I18nType, JsonConfiguration, SameSiteTypes, StorageType, StandardConsent } from "../../services";
import { LogLevelDesc } from "loglevel";
import { IDefaults } from "./config-parameters";
import { ConsentStorageData, ConsentType, FormattedConsentData } from "../../services/consent/consent-type";
import { defaultValues, LinkContainer, LinkTarget } from "../../utils";
import { ViewState } from "./view-state";

@Component({
	tag: "dock-privacy-settings",
	styleUrls: ["dock-privacy-settings.scss"],
	shadow: true,
})
export class DockPrivacySettings implements ComponentInterface, IDefaults {
	/**
	 * Sets the opt-out behavior of DOCK. If enableOptOut is set (true) all default values for the categories are true and checkboxes are checked by default.
	 */
	@Prop({ reflect: true }) enableOptOut: boolean = false;

	/**
	 * New (future-proof) way to set (de-/activate) purpose categories. Prefer to use this, instead of e.g. "disableConsentMarketing". \n
	 * Currently works with standard categories: convenience, analysis, marketing. Put in string or array. \n
	 * If both, `disable-consent...` and `consent-categories`, are used, this one takes precedence!
	 */
	@Prop({ reflect: true }) consentCategories: string | Array<string> = undefined;

	/**
	 * Disable the standard consent category 'convenience'. DEPRECATED. (This will likely be removed in DOCK 5.)
	 * Prefer to use `consentCategories`. If both are used, `consent-categories` takes precedence!
	 */
	@Prop({ reflect: true, mutable: true }) disableConsentConvenience: boolean = false;

	/**
	 * Disable the standard consent category 'analysis' DEPRECATED. (This will likely be removed in DOCK 5.)
	 * Prefer to use `consentCategories`. If both are used, `consent-categories` takes precedence!
	 */
	@Prop({ reflect: true, mutable: true }) disableConsentAnalysis: boolean = false;

	/**
	 * Disable the standard consent category 'marketing' DEPRECATED. (This will likely be removed in DOCK 5.)
	 * Prefer to use `consentCategories`. If both are used, `consent-categories` takes precedence!
	 */
	@Prop({ reflect: true, mutable: true }) disableConsentMarketing: boolean = false;

	/**
	 * Sets the identifier of the cookie so the application can save/read the cookie to/from the browser storage.
	 * If you change this, a new cookie hs to be set -> Your users will be asked for their consent again.
	 */
	@Prop({ reflect: true }) cookieName: string = "do-consent";

	/**
	 * Sets the Same Site property of the cookie with one of three options: Lax, Strict or None.
	 * Set to "none" if you want to use consent on "third-party" site (different domain).
	 */
	@Prop({ reflect: true }) cookieSameSite: SameSiteTypes = "Lax";

	/**
	 * Sets a boolean value for the Secure property of the cookie. Disable this if DOCK is used on non-secure (http only) site.
	 */
	@Prop({ reflect: true }) cookieSecure: boolean = true;

	/**
	 * Sets the Domain property of the cookie. Available options are the current domain and its parent domains.
	 * If domain is set to a different value, the users decision can not be saved!
	 */
	@Prop({ reflect: true }) consentDomain: string = undefined;

	/**
	 * Sets the Path property of the cookie. Cookie is saved only if the set value matches (is part of) the current path
	 */
	@Prop({ reflect: true }) consentPath: string = "/";

	/**
	 * Sets a lifetime value in days for which the cookie will be stored when it is accepted
	 */
	// `defaultValues` can't be referenced here as they wouldn't get compiled into readme. Make sure they stay in sync!
	@Prop({ reflect: true }) consentLifetimeAccepted: number = 365;

	/**
	 * Sets a lifetime value in days for which the cookie will be stored when it is declined
	 */
	@Prop({ reflect: true }) consentLifetimeDeclined: number = 60;

	/**
	 * Disables the 'Decline all' (or 'Reject') button
	 */
	@Prop({ reflect: true }) disableDeclineAll: boolean = false;

	/**
	 * Shows additional text fragment, if data is being processing by third parties.
	 */
	@Prop({ reflect: true }) dataProcessingThirdParty: boolean = true;

	/**
	 * Shows additional text fragment, if data is being processing outside EU.
	 */
	@Prop({ reflect: true }) dataProcessingOutsideEea: boolean = true;

	/**
	 * Contains the current state of all consents formatted to be provided to the user. This should not be configured on the page!
	 * It's an API to dynamically query and use the consents *from* DOCK.
	 */
	@Prop({ mutable: true }) consents: FormattedConsentData;

	/**
	 * Defines whether `prevent-auto-open` feature shall be used: Parameter gets appended to URL on "outgoing" links -> Consent dialog will not show on target page.
	 */
	@Prop({ reflect: true }) preventAutoOpenActive: boolean = true;

	/**
	 * This holds the name of the parameter that determines if the dialog will open
	 */
	// `defaultValues` can't be referenced here as they wouldn't get compiled into readme. Make sure they stay in sync!
	@Prop({ reflect: true }) preventAutoOpenUrlParameter: string = "prevent-auto-open-privacy-settings";

	/**
	 * Disables the language selector
	 */
	// ToDo: Language selector should show by default, but before that, icon bug has to be fixed
	@Prop({ reflect: true }) disableLanguageSelector: boolean = true;

	/**
	 * Disables DOCK's icons
	 */
	@Prop({ reflect: true }) disableIcons: boolean = false;

	/**
	 * Sets the visibility of the cookie manager modal.
	 * This should *not* be set in the "basic config" but is to be used dynamically!!
	 */
	@Prop({ mutable: true, reflect: true }) visible: boolean = false;

	/**
	 * Set log level of logging to the console.
	 */
	@Prop({ reflect: true }) logging: LogLevelDesc = "warn";

	/**
	 * HTML element id of the JSON configuration settings script element block
	 */
	@Prop({ reflect: true }) jsonConfigTag: string = "consent-kit-settings";

	/**
	 * Sets the language/locale of the user interface
	 */
	//ToDo: Check if locale really has to be mutable
	// eslint-disable-next-line @stencil-community/strict-mutable
	@Prop({ mutable: true, reflect: true }) locale: string = undefined;

	/**
	 * Sets the fallback language/locale. This is used if nothing else works
	 */
	@Prop({ reflect: true }) fallbackLocale: string = "en-us";

	/**
	 * Sets the link address (href) of the convenience consent link (read more)
	 */
	@Prop({ reflect: true }) linkUrlConvenience: string = undefined;

	/**
	 * Sets the link address (href) of the analysis consent link (read more)
	 */
	@Prop({ reflect: true }) linkUrlAnalysis: string = undefined;

	/**
	 * Sets the link address (href) of the marketing consent link (read more)
	 */
	@Prop({ reflect: true }) linkUrlMarketing: string = undefined;

	/**
	 * Sets the link address (href) of the footer Policy (privacy notice) link
	 */
	@Prop({ reflect: true }) linkUrlPolicy: string = undefined;

	/**
	 * Sets the link address (href) of the footer Imprint (corporate information) link
	 */
	@Prop({ reflect: true }) linkUrlImprint: string = undefined;

	/**
	 * Controls if the links are going to open in a new tab/window
	 */
	@Prop({ reflect: true, mutable: true }) linksTarget: "_blank" | "_self" = "_self";

	/**
	 * Select the type of Google Consent Mode integration. NOTE: Default is "gtmApi" but we might change this to "none" in DOCK 5.
	 * So, it's recommended to configure this explicitly!
	 */
	@Prop({ reflect: true }) googleConsentModeIntegration: GoogleConsentApiType = "gtmApi";

	/**
	 * Activate additional requests to generate DOCK statistics (e.g. Opt-in rate).
	 * This will likely produce a 404 error in the user's browser console (for now)!
	 */
	@Prop({ reflect: true }) experimentalActivateStatisticsRequests: boolean = false;

	// TODO: Check "internalConsents" implementation (everything) for potential memory leak etc.!!!
	// See also jest bug https://github.com/jestjs/jest/issues/11956
	/**
	 * Contains the current state of all consents
	 */
	@State() internalConsents: ConsentType[] = [
		{
			name: StandardConsent.Convenience,
			userConsents: this.enableOptOut,
		},
		{
			name: StandardConsent.Analysis,
			userConsents: this.enableOptOut,
		},
		{
			name: StandardConsent.Marketing,
			userConsents: this.enableOptOut,
		},
	];

	// ToDo: Consider merging this with (internal) consents in one "consent (storage) data" object
	/**
	 * Contains the current state of the consent storage (meta) data
	 */
	@State() consentMetaData: ConsentStorageData;

	/**
	 * List of all active categories
	 */
	@State() internalCategoryList: string[];

	/**
	 * Holds the current state of the view
	 */
	@State() viewState: ViewState = ViewState.InitialView;

	/**
	 * Holds the internal current locale state
	 */
	@State() currentLocale: string = undefined;

	/**
	 * Holds the internal current ui texts for display in the modal
	 */
	@State() localizationContent: I18nType = undefined;

	/**
	 * Holds the read JSON configuration
	 */
	@State() jsonConfiguration: JsonConfiguration = undefined;

	@State() footerLinks: LinkContainer;

	/**
	 * This is the Cookie Manager element
	 * @private
	 */
	@Element() private element: HTMLDockPrivacySettingsElement;

	/**
	 * Version of dock storage cookie format.
	 */
	private dockCookieVersion: number = 4;

	/**
	 * Watches the 'logging' attribute for value changes and sets the application log level
	 * @param newLogSetting The new log level setting
	 */
	@Watch("logging")
	validateAndSetLogLevel(newLogSetting): void {
		if (LogService.isOfTypeLogLevelDesc(newLogSetting)) {
			LogService.log.setLevel("info");
			LogService.log.info("Set log level to:", newLogSetting);
			LogService.log.setLevel(newLogSetting);
		} else if (newLogSetting !== null) {
			LogService.log.setLevel("warn");
			LogService.log.warn("Log level is incorrect, fallback to 'silent'");
			LogService.log.setLevel("silent");
		}
	}

	/**
	 * Watches the 'locale' attribute for value changes
	 * @param newLocale
	 */
	@Watch("locale")
	async changeLocale(newLocale: string): Promise<void> {
		LogService.log.info("Apply changed locale:", newLocale);
	}

	/**
	 * Watches the 'linksInNewTab' attribute for value changes
	 * @param newValue
	 */
	@Watch("linksTarget")
	async changeLinksInNewTab(newValue: boolean): Promise<void> {
		LogService.log.info("Opening of links in new tab:", newValue);
		this.propertyValidations();
	}

	/**
	 * Watches the 'convenience-link-url' attribute for value changes
	 * @param newLinkAddress The new link address
	 */
	@Watch("linkUrlConvenience")
	async setConvenienceLink(newLinkAddress: string): Promise<void> {
		LogService.log.info("Changed convenience link address:", newLinkAddress);
	}

	/**
	 * Watches enableOptOut property and updates the internal consent values
	 * @param newValue
	 */
	@Watch("enableOptOut")
	updateEnableOptOut(newValue: boolean) {
		LogService.log.debug(`enableOptOut was changed to ${newValue}`);

		this.internalConsents = this.internalConsents.map(category => {
			return { name: category.name, userConsents: newValue };
		});
	}

	/**
	 * Watches the 'analysis-link-url' attribute for value changes
	 * @param newLinkAddress The new link address
	 */
	@Watch("linkUrlAnalysis")
	async setAnalysisLink(newLinkAddress: string): Promise<void> {
		LogService.log.info("Changed convenience link address:", newLinkAddress);
	}

	/**
	 * Watches the 'marketing-link-url' attribute for value changes
	 * @param newLinkAddress The new link address
	 */
	@Watch("linkUrlMarketing")
	async setMarketingLink(newLinkAddress: string): Promise<void> {
		LogService.log.info("Changed marketing link address:", newLinkAddress);
	}

	/**
	 * Watches the 'link-url-policy' attribute for value changes
	 * @param newLinkAddress The new link address
	 */
	@Watch("linkUrlPolicy")
	async setPolicyLink(newLinkAddress: string): Promise<void> {
		LogService.log.info("Changed policy link address:", newLinkAddress);
	}

	/**
	 * Watches the 'link-url-imprint' attribute for value changes
	 * @param newLinkAddress The new link address
	 */
	@Watch("linkUrlImprint")
	async setImprintLink(newLinkAddress: string): Promise<void> {
		LogService.log.info("Changed imprint link address:", newLinkAddress);
	}

	/**
	 * Watches the 'prevent-auto-open-active' attribute for value changes
	 * @param newPreventAutoOpenActive
	 */
	@Watch("preventAutoOpenActive")
	async setPreventAutoOpenActive(newPreventAutoOpenActive: boolean): Promise<void> {
		LogService.log.info("Changed prevent auto-open active:", newPreventAutoOpenActive);
		await this.checkConsentAvailabilityAndSetModalVisibilityAndState();
	}

	/**
	 * Watches the 'prevent-auto-open-url-parameter' attribute for value changes
	 * @param newUrlParameter The new url parameter
	 */
	@Watch("preventAutoOpenUrlParameter")
	async setPreventAutoOpenUrlParameter(newUrlParameter: string): Promise<void> {
		LogService.log.info("Changed prevent auto-open URL parameter:", newUrlParameter);
	}

	/**
	 * Watches consentCategories and builds internalCategoryList from it (or from disableConsent... props, if consentCategories is not set)
	 * @param consentCategories The updated string or array containing the configured consent categories
	 */
	@Watch("consentCategories")
	createCategoryListFromConfig(consentCategories: string | Array<string>) {
		LogService.log.debug("Trying to build enabled categories list:", consentCategories);
		let activeCategoryList: string[];

		if (consentCategories === undefined) {
			LogService.log.debug(`No consent categories config found. Building from defaults (disabled attributes)`);
			activeCategoryList = this.createCategoryListFromConfigDisabledCategories();
		} else {
			LogService.log.debug(`Consent categories config found: '${consentCategories}'`);
			activeCategoryList = this.createCategoryListFromConfigConsentCategories(consentCategories);
		}

		this.internalCategoryList = activeCategoryList;
	}

	/**
	 * Watches disableConsent... props calls createCategoryListFromConfig
	 * @param newConsentCategoriesStringOrArray: The updated string or array containing the configured consent categories
	 */

	/**
	 * Watches disableConsent... props calls createCategoryListFromConfig and logs change
	 * @param newValue
	 * @param oldValue
	 * @param propName
	 */
	@Watch("disableConsentConvenience")
	@Watch("disableConsentAnalysis")
	@Watch("disableConsentMarketing")
	updateInternalConsents(newValue: boolean, oldValue: boolean, propName: string) {
		LogService.log.debug(`${propName} was changed from ${oldValue} to ${newValue}`);
		this.createCategoryListFromConfig(this.consentCategories);
	}

	@Watch("disableConsentConvenience")
	// This (and next) method only gets called when category is disabled/enabled "live"
	async removeOrAddConsentConvenience(disabled: boolean) {
		if (disabled) {
			LogService.log.debug(`Disabled standard consent '${StandardConsent.Convenience}'`);
		} else if (
			!this.internalConsents.includes({
				name: StandardConsent.Convenience,
				userConsents: this.enableOptOut,
			})
		) {
			LogService.log.debug(`Enabled standard consent '${StandardConsent.Convenience}'`);
		}
	}

	@Watch("disableConsentMarketing")
	// This (and previous) method only gets called when category is disabled/enabled "live"
	async removeOrAddConsentMarketing(disabled: boolean) {
		if (disabled) {
			LogService.log.debug(`Disabled standard consent '${StandardConsent.Marketing}'`);
		} else if (
			!this.internalConsents.includes({
				name: StandardConsent.Marketing,
				userConsents: this.enableOptOut,
			})
		) {
			LogService.log.debug(`Enabled standard consent '${StandardConsent.Marketing}'`);
		}
	}

	@Watch("disableConsentAnalysis")
	// This (and previous) method only gets called when category is disabled/enabled "live"
	async removeOrAddConsentAnalysis(disabled: boolean) {
		if (disabled) {
			LogService.log.debug(`Disabled standard consent '${StandardConsent.Analysis}'`);
		} else if (
			!this.internalConsents.includes({
				name: StandardConsent.Analysis,
				userConsents: this.enableOptOut,
			})
		) {
			LogService.log.debug(`Enabled standard consent '${StandardConsent.Analysis}'`);
		}
	}

	/**
	 * Sets enabled (or non-disabled) categories (disableConsent... props) from internalCategoryList.
	 * @param consentListArray
	 */
	@Watch("internalCategoryList")
	setEnabledConsentsFromCategoryList(consentListArray: Array<string>) {
		let disableAnalysis = true;
		let disableConvenience = true;
		let disableMarketing = true;

		for (const item in consentListArray) {
			switch (consentListArray[item]) {
				case "convenience":
					LogService.log.debug(`Enabled consent convenience`);
					disableConvenience = false;
					break;
				case "analysis":
					LogService.log.debug(`Enabled consent analysis`);
					disableAnalysis = false;
					break;
				case "marketing":
					LogService.log.debug(`Enabled consent marketing`);
					disableMarketing = false;
					break;
			}
		}

		this.disableConsentConvenience = disableConvenience;
		this.disableConsentAnalysis = disableAnalysis;
		this.disableConsentMarketing = disableMarketing;
	}

	/**
	 * Watches the 'disable-decline-all' attribute for value changes
	 * @param disableDeclineAll
	 */
	@Watch("disableDeclineAll")
	async toggleDeclineAllButtonVisibility(disableDeclineAll: boolean): Promise<void> {
		LogService.log.info("Disabled 'Decline all' button", disableDeclineAll);
	}

	/**
	 * Watches consent-path attribute for value changes
	 */
	@Watch("consentPath")
	consentPathValidation() {
		this.setModalVisibility();
		LogService.log.info("Consent path has been changed");
	}

	/**
	 * Watches consent-domain attribute for value changes
	 */
	@Watch("consentDomain")
	consentDomainValidation() {
		this.setModalVisibility();
		LogService.log.info("Consent domain has been changed");
	}

	/**
	 * Watches cookie-same-site attribute for value changes
	 */
	@Watch("cookieSameSite")
	cookieSameSiteValidation() {
		this.propertyValidations();
		LogService.log.info("Cookie same site has been changed");
	}

	/**
	 * Watches consent-lifetime-declined attribute for value changes
	 */
	@Watch("consentLifetimeDeclined")
	consentLifetimeDeclinedValidation() {
		this.propertyValidations();
		LogService.log.info("Consent lifetime declined has been changed");
	}

	/**
	 * Watches consent-lifetime-accepted attribute for value changes
	 */
	@Watch("consentLifetimeAccepted")
	consentLifetimeAcceptedValidation() {
		this.propertyValidations();
		LogService.log.info("Consent lifetime accepted has been changed");
	}

	/**
	 * Listens to the 'modalShowEvent' event from the modal
	 * @param event
	 */
	@Listen("modalShowEvent")
	modalShowEventHandler(event) {
		LogService.log.debug(event);
	}

	/**
	 * Listens for locale change from the language selector
	 * @param newLocale
	 */
	@Listen("localeChangeEvent")
	async languageSelectorLocaleChange(newLocale): Promise<void> {
		LogService.log.info("Apply changed locale:", newLocale);
		this.locale = newLocale.detail;
	}

	/**
	 * Listens to the 'modalHideEvent' event from the modal
	 * @param event
	 */
	@Listen("modalHideEvent")
	modalHideEventHandler(event) {
		LogService.log.debug(event);
	}

	/**
	 * Listens to the 'changeToPrivacySettingsLinkEditViewEvent' event from the modal
	 * @param event
	 */
	// ToDo: Rename!
	@Listen("changeToPrivacySettingsLinkEditViewEvent")
	async changeToPrivacySettingsLinkEditViewEventHandler(event) {
		LogService.log.debug("Clicked privacy 'settings'", event);
		this.viewState = ViewState.EditView;
	}

	/**
	 * Listens to the 'changeToPrivacySettingsLinkInitialViewEvent' event from the modal
	 * @param event
	 */
	// ToDo: Rename!
	@Listen("changeToPrivacySettingsLinkInitialViewEvent")
	async changeToPrivacySettingsLinkInitialViewEventHandler(event) {
		LogService.log.debug("Clicked privacy 'settings'", event);
		this.viewState = ViewState.InitialView;
	}

	/**
	 * Listens to the 'saveAcceptAllEvent' event from the modal 'accept all' button
	 * @param event
	 */
	@Listen("saveAcceptAllEvent")
	async saveAcceptAllEventHandler(event) {
		LogService.log.debug("Clicked 'Accept All':", event);

		let convenienceValue = !this.disableConsentConvenience;
		let analysisValue = !this.disableConsentAnalysis;
		let marketingValue = !this.disableConsentMarketing;

		this.internalConsents = [
			{ name: "convenience", userConsents: convenienceValue },
			{ name: "analysis", userConsents: analysisValue },
			{ name: "marketing", userConsents: marketingValue },
		];
		await this.syncConsents();
		await this.executeGlobalCallbackAndUpdateGoogleConsents();
		await ConsentService.storeConsents(
			this.internalConsents,
			this.dockCookieVersion,
			[StorageType.Cookie],
			this.cookieName,
			this.cookieSameSite,
			this.cookieSecure,
			this.consentDomain,
			this.consentPath,
			this.consentLifetimeAccepted,
		);
		await this.setModalVisibilityAndState(false, ViewState.EditView);

		if (this.experimentalActivateStatisticsRequests) {
			let requestUrl: string = "../savedacceptall";
			await I18nService.fetchForStatistics(requestUrl);
		}
	}

	/**
	 * Listens to the 'saveDeclineAllEvent' event from the modal 'reject/decline all' button
	 * @param event
	 */
	@Listen("saveDeclineAllEvent")
	async saveDeclineAllEventHandler(event) {
		LogService.log.debug("Clicked 'Decline All':", event);
		this.internalConsents = [
			{ name: "convenience", userConsents: false },
			{ name: "analysis", userConsents: false },
			{ name: "marketing", userConsents: false },
		];
		await this.syncConsents();
		await this.executeGlobalCallbackAndUpdateGoogleConsents();
		await ConsentService.storeConsents(
			this.internalConsents,
			this.dockCookieVersion,
			[StorageType.Cookie],
			this.cookieName,
			this.cookieSameSite,
			this.cookieSecure,
			this.consentDomain,
			this.consentPath,
			this.consentLifetimeDeclined,
		);
		await this.setModalVisibilityAndState(false, ViewState.EditView);

		if (this.experimentalActivateStatisticsRequests) {
			let requestUrl: string = "../saveddeclineall";
			await I18nService.fetchForStatistics(requestUrl);
		}
	}

	/**
	 * Listens to the 'saveCurrentSelectionEvent' event from the modal 'save' button.
	 * On event the consents in their current state are saved to the storage.
	 * @param event
	 */
	@Listen("saveCurrentSelectionEvent")
	async saveCurrentSelectionEventHandler(event) {
		LogService.log.debug("Clicked 'Save'", event);
		await this.syncConsents();
		await this.executeGlobalCallbackAndUpdateGoogleConsents();
		let consentLifetime = this.consentLifetimeDeclined;
		for (const consent of this.internalConsents) {
			if (consent.userConsents) {
				consentLifetime = this.consentLifetimeAccepted;
			}
		}
		await ConsentService.storeConsents(
			this.internalConsents,
			this.dockCookieVersion,
			[StorageType.Cookie],
			this.cookieName,
			this.cookieSameSite,
			this.cookieSecure,
			this.consentDomain,
			this.consentPath,
			consentLifetime,
		);
		await this.setModalVisibilityAndState(false, ViewState.EditView);

		if (this.experimentalActivateStatisticsRequests) {
			let requestUrl: string = "../saved";
			requestUrl += this.consents.convenience ? "conv" : "";
			requestUrl += this.consents.analysis ? "anal" : "";
			requestUrl += this.consents.marketing ? "mark" : "";
			await I18nService.fetchForStatistics(requestUrl);
		}
	}

	/**
	 * Listens to the 'checkboxToggleModalEvent' event from the checkboxes.
	 * @param event
	 */
	@Listen("checkboxToggleModalEvent")
	checkboxToggleModalEventHandler(event) {
		LogService.log.debug(event);
		this.internalConsents = this.internalConsents.map(consent => {
			return consent.name === event.detail.name ? { name: consent.name, userConsents: event.detail.userConsents } : consent;
		});
	}

	connectedCallback() {
		globalThis.addConsentUpdateCallback = GoogleConsentService.addConsentUpdateCallback;
		globalThis.getConsentUpdateListener = GoogleConsentService.getConsentUpdateCallbacks;
	}

	/**
	 * Called once just after the component is first connected to the DOM - only called once
	 */
	async componentWillLoad() {
		// Read JSON configuration if available
		this.jsonConfiguration = JsonConfigurationService.getConfiguration(this.jsonConfigTag);
		this.createCategoryListFromConfig(this.consentCategories);
		LogService.log.setLevel(this.logging);
		LogService.log.debug("The standard consent link values are:", this.linkUrlConvenience, this.linkUrlAnalysis, this.linkUrlMarketing);

		const usedStorageType: StorageType = StorageType.Cookie;
		const restoredConsents = await ConsentService.restoreConsents([usedStorageType], this.cookieName);

		this.applyRestoredConsents(restoredConsents);
		await this.checkConsentAvailabilityAndSetModalVisibilityAndState();
		LogService.log.debug("DOCK Privacy Settings dialog is visible:", this.visible);

		await this.syncConsents();
		GoogleConsentService.setDefaultConsents(this.consents, this.googleConsentModeIntegration);
		await this.executeGlobalCallbackAndUpdateGoogleConsents();

		// NOTE: To add new custom consent categories to an existing storage/cookie they will have to be injected here
	}

	/**
	 * Will be called when the component will render
	 */
	async componentWillRender() {
		// Checks if the modal is visible and (no locale has been loaded yet or (the locale has changed))
		if (this.visible && (this.currentLocale === undefined || (this.currentLocale !== this.locale && this.locale !== undefined))) {
			await this.fetchAndSaveLanguage(this.locale);
		}

		this.footerLinks = {};
		if (this.linkUrlPolicy) {
			this.footerLinks.policy = { name: "policy", linkUrl: this.linkUrlPolicy };
		}
		if (this.linkUrlImprint) {
			this.footerLinks.imprint = { name: "imprint", linkUrl: this.linkUrlImprint };
		}
	}

	/**
	 * Renders the web component
	 */
	render() {
		return this.visible ? (
			<div>
				<span slot="content" />
				<modal-view
					show={this.visible}
					localizationContent={this.localizationContent}
					consentData={this.internalConsents}
					categoryLinksData={{
						convenience: { name: "convenience", linkUrl: this.linkUrlConvenience },
						analysis: { name: "analysis", linkUrl: this.linkUrlAnalysis },
						marketing: { name: "marketing", linkUrl: this.linkUrlMarketing },
					}}
					footerLinksData={this.footerLinks}
					viewState={this.viewState}
					disabledConvenience={this.disableConsentConvenience}
					disabledAnalysis={this.disableConsentAnalysis}
					disabledMarketing={this.disableConsentMarketing}
					disableDeclineAll={this.disableDeclineAll}
					preventAutoOpen={this.preventAutoOpenActive}
					preventAutoOpenUrlParameter={this.preventAutoOpenUrlParameter}
					disableIcons={this.disableIcons}
					dataProcessingThirdParty={this.dataProcessingThirdParty}
					dataProcessingOutsideEea={this.dataProcessingOutsideEea}
					linksTarget={this.linksTarget as LinkTarget}
					disableLanguageSelector={this.disableLanguageSelector}
				/>
			</div>
		) : (
			""
		);
	}

	/**
	 * Will be called when the component has been loaded
	 */
	async componentDidLoad() {
		LogService.log.info("DOCK privacy settings was loaded");
	}

	/**
	 * Execute global callback "BoschPrivacyCallback_OnUpdate" if defined, with current consents as parameter.
	 * @param consents Current consents that the callback will be called with
	 */
	private executeGlobalCallback(consents: FormattedConsentData) {
		if (typeof (window as any).BoschPrivacyCallback_OnUpdate === "function") {
			LogService.log.debug("Executing global callback with consents: ", consents);
			(window as any).BoschPrivacyCallback_OnUpdate(consents);
		}
	}

	/**
	 * Converts and syncs saved "internalConsents" (state) to the exposed "consents" (prop)
	 */
	private async syncConsents() {
		//Workaround for opt-out. Make sure disabled categories are disabled.
		this.disableConsentConvenience ? (this.internalConsents.find(element => element.name === StandardConsent.Convenience).userConsents = false) : "";
		this.disableConsentAnalysis ? (this.internalConsents.find(element => element.name === StandardConsent.Analysis).userConsents = false) : "";
		this.disableConsentMarketing ? (this.internalConsents.find(element => element.name === StandardConsent.Marketing).userConsents = false) : "";

		this.consents = {
			convenience: this.internalConsents.find(element => element.name === StandardConsent.Convenience).userConsents,
			marketing: this.internalConsents.find(element => element.name === StandardConsent.Marketing).userConsents,
			analysis: this.internalConsents.find(element => element.name === StandardConsent.Analysis).userConsents,
		};
	}

	private async executeGlobalCallbackAndUpdateGoogleConsents() {
		await GoogleConsentService.updateConsents(this.consents, this.googleConsentModeIntegration);
		this.executeGlobalCallback(this.consents);
	}

	/**
	 * Requests the specified locale file from the server and stores it in this.uiTextContent
	 * @param locale
	 */
	private async fetchAndSaveLanguage(locale: string): Promise<void> {
		LogService.log.debug("fetchAndSaveLanguage: Given locale is:", locale);

		const initialLoad: boolean = this.currentLocale == undefined;
		const changedLocale: string = I18nService.getLocale(this.fallbackLocale, this.element, locale);
		this.currentLocale = changedLocale;

		await I18nService.fetchI18n(changedLocale, initialLoad)
			.then(async content => {
				LogService.log.debug("Successfully requested the locale content:", content);

				// Save the current content for further use
				this.localizationContent = content;

				// override configured texts of the current locale if available in the JSON configuration
				if (this.jsonConfiguration && this.jsonConfiguration.i18nOverride && this.currentLocale in this.jsonConfiguration.i18nOverride) {
					this.localizationContent = deepmerge(content, this.jsonConfiguration.i18nOverride[this.currentLocale]);

					LogService.log.debug(
						`Overriding locale '${this.currentLocale}' texts with selected JSON configuration texts:`,
						this.jsonConfiguration.i18nOverride[this.currentLocale],
					);
				}
			})
			.catch(error => {
				LogService.log.error(error);
			});
	}

	/**
	 * Sets the 'visible' and 'viewState' state properties
	 * @param visible
	 * @param viewState
	 * @private
	 */
	private async setModalVisibilityAndState(visible: boolean, viewState: ViewState) {
		LogService.log.debug("Set modal visible and modal state:", visible, viewState);
		this.setModalVisibility(visible);
		this.viewState = viewState;
		this.propertyValidations();
	}

	/**
	 * Validates consent-domain, consent-path and prevent-auto-open-url-parameter after which sets the visibility of the
	 * modal and prints a corresponding message.
	 */
	private setModalVisibility(visible?: boolean) {
		if (visible !== undefined) {
			this.visible = visible;
		}

		if (this.visible !== false) {
			// we only have to go through these checks, if visible is not already false
			LogService.log.debug("Checking if DOCK should be invisible due to location and config.");
			LogService.log.debug("LOCATION (host (domain), pathname, search (URL params)):", location.host, location.pathname, location.search);
			LogService.log.debug(
				"CONFIGURATION (consentDomain, consentPath, preventAutoOpenParameter, preventAutoOpenActive):",
				this.consentDomain,
				this.consentPath,
				this.preventAutoOpenUrlParameter,
				this.preventAutoOpenActive,
			);

			if (this.consentDomain && !location.host.endsWith(this.consentDomain)) {
				LogService.log.error("The configured domain " + this.consentDomain + " does not match URL host. Website data can only be stored and accessed for the same origin!");
				this.visible = false;
				return;
			}
			if (this.consentPath && location.pathname.indexOf(this.consentPath) !== 0) {
				LogService.log.warn("consent-path was set to " + this.consentPath + ". Consent will only be available on that path and children of it");
				this.visible = false;
				return;
			}
			const currentSearchParams = new URLSearchParams(location.search);
			if (currentSearchParams.has(this.preventAutoOpenUrlParameter) && this.preventAutoOpenActive) {
				LogService.log.info(
					`Prevent-auto-open parameter '${this.preventAutoOpenUrlParameter}' found in URL Search Params (and prevent-auto-open is not deactivated). Not showing DOCK!`,
				);
				this.visible = false;
			}
		}
	}

	/**
	 * Validates consent-lifetime-accepted, consent-lifetime-declined and cookie-same-site, prints a corresponding message.
	 */
	private propertyValidations() {
		const siteTypesList: string[] = defaultValues.siteTypesList;

		if (!siteTypesList.includes(this.cookieSameSite)) {
			LogService.log.error("The cookie same-site attribute is invalid, setting to default value (Lax).");
			this.cookieSameSite = defaultValues.cookieSameSite;
		}
		if (this.consentLifetimeDeclined < 1 && this.consentLifetimeDeclined > 1000) {
			LogService.log.error("The cookie-declined-lifetime attribute should be a number between 1 and 1000.");
			this.consentLifetimeDeclined = defaultValues.lifetimeDeclined;
		}
		if (this.consentLifetimeAccepted < 1 && this.consentLifetimeAccepted > 1000) {
			LogService.log.error("The cookie-accepted-lifetime attribute should be a number between 1 and 1000.");
			this.consentLifetimeAccepted = defaultValues.lifetimeAccepted;
		}
		if (this.linksTarget !== "_blank" && this.linksTarget !== "_self") {
			LogService.log.error(`The links-target attribute is invalid. It must be '_self' or '_blank' setting to default value (${defaultValues.linksTarget}).`);
			this.linksTarget = defaultValues.linksTarget as "_blank" | "_self";
		}
	}

	/**
	 * Checks if the restored consents are empty and sets them as the new state of consents if available.
	 * In addition, the modal visibility is set accordingly. If there is a consent available already,
	 * the modal is not shown.
	 * @private
	 */
	private async checkConsentAvailabilityAndSetModalVisibilityAndState() {
		LogService.log.debug("Checking consent availability and setting modal visibility and state accordingly.");
		this.internalConsents.length > 0 && this.consentMetaData && this.consentMetaData.version === this.dockCookieVersion
			? await this.setModalVisibilityAndState(false, ViewState.EditView)
			: await this.setModalVisibilityAndState(true, ViewState.InitialView);
	}

	/**
	 * Applies restored consents to internal consents. Maps categories correctly by name.
	 * @param restoredConsents
	 * @private
	 */
	private applyRestoredConsents(restoredConsents: { storageData: ConsentStorageData; consents: ConsentType[] }) {
		LogService.log.debug("Applying restored consents:", restoredConsents);
		this.internalConsents = this.internalConsents.map(consent => {
			return restoredConsents.consents.some(element => element.name === consent.name) ? restoredConsents.consents.find(element => element.name === consent.name) : consent;
		});
		this.consentMetaData = restoredConsents.storageData;
	}

	/**
	 * Creates a category list (array) from a string or array of configured categories
	 * @param consentCategories
	 * @private
	 */
	private createCategoryListFromConfigConsentCategories(consentCategories: string | Array<string>): string[] {
		LogService.log.debug("Trying to build category list from array or string:", consentCategories);
		let categoryList = [];

		if (consentCategories !== undefined) {
			LogService.log.debug(`Consent categories config found: '${consentCategories}'`);

			if (typeof consentCategories === "string") {
				LogService.log.debug(`Defined categories are in a string`);
				let consentList = consentCategories.replaceAll(/\s/g, "");
				categoryList = consentList.split(",");
			} else if (Array.isArray(consentCategories)) {
				LogService.log.debug(`Defined categories are in an array`);
				categoryList = consentCategories;
			} else {
				LogService.log.debug(`Defined categories are neither string nor array! Using defaults!`);
				// TODO: Not sure if we should do this... Or should we just tell the integrator that the config is shit!?
				categoryList = this.createCategoryListFromConfigDisabledCategories();
			}
		}

		return categoryList;
	}

	/**
	 * Creates a category list from non-disabled consent categories. Uses the disableConsent... props.
	 * @private
	 */
	private createCategoryListFromConfigDisabledCategories() {
		// Create list from defaults, remove disabled categories
		// Just as intermediate solution, until disableConsent... attributes are removed
		LogService.log.debug(`Creating internal category list from defaults / disableConsent... attributes.`);

		const convenience = this.disableConsentConvenience ? undefined : "convenience";
		const analysis = this.disableConsentAnalysis ? undefined : "analysis";
		const marketing = this.disableConsentMarketing ? undefined : "marketing";

		return [convenience, analysis, marketing];
	}
}
